import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProfileSort } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

import type { ProfilesPage } from '../infrastructure/tracked-lps.gateway';
import { initialState } from './tracked-lps.state';

/**
 * Tracked LPs list slice. Reducers are pure state transitions only; the
 * paginated fetch lives in the saga, which reads `page` / `pageSize` / `filters`
 * back from this state and dispatches these transitions as it progresses.
 * `filtersSet` / `pageSizeSet` / `sortSet` reset to the first page (mirroring the old
 * `useInfiniteApi`); the saga triggers the refetch.
 */
const slice = createSlice({
  name: 'trackedLps',
  initialState,
  reducers: {
    // ── Cursor + filters (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },
    filtersSet: (state, action: PayloadAction<FilterGroup | null>) => {
      state.filters = action.payload;
      state.page = 0;
    },
    sortSet: (state, action: PayloadAction<ProfileSort | null>) => {
      state.sort = action.payload;
      state.page = 0;
    },

    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<ProfilesPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.loading = false;
      state.loaded = true;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ── Optimistic un-favorite (replaces the legacy `removedIds` filter memo) ──
    rowRemoved: (state, action: PayloadAction<string>) => {
      const before = state.rows.length;
      state.rows = state.rows.filter((row) => row.id !== action.payload);
      if (state.rows.length < before && state.total > 0) state.total -= 1;
    },
  },
});

export const trackedLpsActions = slice.actions;
export const trackedLpsReducer = slice.reducer;
